import { useCallback } from "react";
import { logEvent } from "../../../../../telemetry";
// Host-agnostic despite the folder name: this is the app's only toast bridge,
// and its sink is the chat panel's <ChatToaster>, which mounts for every host.
import { emitToast } from "../../../../../../integrations/tools/outlook/toast-bridge";
import { chipSheet, chipTargetView, type ChipSheetId, type ChipTargetView } from "../chipTargets";
import type { ActionChip } from "../types";

/** Matches the connector name used by the rest of Deckorate's telemetry. */
const CHIPS_CONNECTOR = "deckorate";

/** Opens a Deckorate screen — supplied by TaskPane, which owns the active tab. */
export type OpenDeckorateView = (view: ChipTargetView) => void;

/** Opens an in-pane sheet — supplied by ChatTabPanel, which hosts them. */
export type OpenChipSheet = (sheet: ChipSheetId) => void;

/**
 * Turns a chip click into the action behind it.
 *
 */
export function useActionChipDispatch(
  onOpenDeckorateView?: OpenDeckorateView,
  onOpenChipSheet?: OpenChipSheet,
): (chip: ActionChip) => void {
  return useCallback(
    (chip: ActionChip) => {
      // Sheets are checked first: chipTargets guarantees a chip is in at most
      // one of the two maps, and this is the order that documents which wins
      // if that ever stops being true.
      const sheet = chipSheet(chip.id);
      if (sheet && onOpenChipSheet) {
        logEvent("connector.action.invoke", "usage", {
          connectorSystemName: CHIPS_CONNECTOR,
          reason: `action-chip-sheet-${chip.id}`,
        });
        onOpenChipSheet(sheet);
        return;
      }

      const view = chipTargetView(chip.id);
      if (view && onOpenDeckorateView) {
        logEvent("connector.action.invoke", "usage", {
          connectorSystemName: CHIPS_CONNECTOR,
          reason: `action-chip-open-${chip.id}`,
        });
        onOpenDeckorateView(view);
        return;
      }

      // Usage-level, not a warning: an un-wired chip is the expected state for
      // most of the tray right now, and the counts tell us which placeholders
      // users actually reach for — i.e. which to implement next.
      logEvent("connector.action.invoke", "usage", {
        connectorSystemName: CHIPS_CONNECTOR,
        reason: `action-chip-placeholder-${chip.id}`,
      });
      emitToast({ title: `${chip.label} is not available yet.`, intent: "info" });
    },
    [onOpenDeckorateView, onOpenChipSheet],
  );
}
